// Company stores (S4 shop, 2026-10-12) — the cart, kept in this browser.
//
// One cart per store AND person (localStorage key names both), so two people
// sharing a computer, or one person in two stores, never mix carts. It holds
// only what the person chose — products, colours, sizes, the address, how to
// pay — never a price: every price is asked of the server.
//
// The idempotency key belongs to the cart. It stays the same while the cart
// stays the same (a double click, or a retry after a dropped connection, is
// the SAME order on the server), and is replaced when the cart's contents
// change and after an order goes through.
//
// localStorage can throw (private mode, quota, disabled storage): every read
// and write is wrapped, and the cart then simply lives in memory.
import { useCallback, useSyncExternalStore } from "react";
import type { PaymentOption } from "@/lib/portal-store";
import { newIdempotencyKey, type CartItem, type ShipTo } from "@/lib/store-shop";

export interface StoreCart {
  v: 1;
  items: CartItem[];
  shipTo: ShipTo | null;
  paymentMethod: PaymentOption | null;
  idempotencyKey: string;
}

const EVENT = "other-store-cart";
const memory = new Map<string, StoreCart>();
const snapshots = new Map<string, { raw: string; cart: StoreCart }>();

export function cartStorageKey(storeId: string, userId: string): string {
  return `other.storeCart.v1:${storeId}:${userId}`;
}

function emptyCart(): StoreCart {
  return { v: 1, items: [], shipTo: null, paymentMethod: null, idempotencyKey: newIdempotencyKey() };
}

function isCart(x: unknown): x is StoreCart {
  const c = x as StoreCart | null;
  return !!c && c.v === 1 && Array.isArray(c.items) && typeof c.idempotencyKey === "string";
}

function readRaw(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** The cart for this key — the same object until it changes (for React). */
export function readCart(key: string): StoreCart {
  const raw = readRaw(key);
  if (raw === null) {
    const m = memory.get(key);
    if (m) return m;
    const fresh = emptyCart();
    memory.set(key, fresh);
    return fresh;
  }
  const hit = snapshots.get(key);
  if (hit && hit.raw === raw) return hit.cart;
  let cart: StoreCart;
  try {
    const parsed = JSON.parse(raw) as unknown;
    cart = isCart(parsed) ? parsed : emptyCart();
  } catch {
    cart = emptyCart();
  }
  snapshots.set(key, { raw, cart });
  return cart;
}

function contents(c: StoreCart): string {
  return JSON.stringify([c.items, c.shipTo, c.paymentMethod]);
}

export function writeCart(key: string, next: StoreCart): void {
  const prev = readCart(key);
  // A different cart is a different order: a new key.
  const cart = contents(prev) !== contents(next) && prev.idempotencyKey === next.idempotencyKey
    ? { ...next, idempotencyKey: newIdempotencyKey() }
    : next;
  memory.set(key, cart);
  try {
    const raw = JSON.stringify(cart);
    window.localStorage.setItem(key, raw);
    snapshots.set(key, { raw, cart });
  } catch {
    // Storage unavailable: the in-memory copy is the cart.
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: key }));
}

function subscribe(cb: () => void): () => void {
  window.addEventListener(EVENT, cb);
  window.addEventListener("storage", cb);
  return () => {
    window.removeEventListener(EVENT, cb);
    window.removeEventListener("storage", cb);
  };
}

/** Pieces in the cart (for the nav). */
export function cartPieces(c: StoreCart): number {
  return c.items.reduce(
    (n, i) => n + i.colorQuantities.reduce((m, r) => m + Object.values(r.quantities).reduce((a, b) => a + (Number(b) > 0 ? Number(b) : 0), 0), 0),
    0,
  );
}

/** The cart for (store, person). `key` null = not known yet (empty cart). */
export function useStoreCart(storeId: string | null | undefined, userId: string | null | undefined) {
  const key = storeId && userId ? cartStorageKey(storeId, userId) : null;
  const cart = useSyncExternalStore(
    subscribe,
    () => (key ? readCart(key) : EMPTY),
    () => EMPTY,
  );
  const update = useCallback(
    (fn: (c: StoreCart) => StoreCart) => {
      if (!key) return;
      writeCart(key, fn(readCart(key)));
    },
    [key],
  );
  /** After an order goes through: an empty cart and a fresh key (the
   *  address and payment choice are kept for next time). */
  const clearAfterOrder = useCallback(() => {
    if (!key) return;
    const c = readCart(key);
    writeCart(key, { ...emptyCart(), shipTo: c.shipTo, paymentMethod: c.paymentMethod });
  }, [key]);
  /** The server said the key is spent: same cart, new key. */
  const renewKey = useCallback(() => {
    if (!key) return;
    writeCart(key, { ...readCart(key), idempotencyKey: newIdempotencyKey() });
  }, [key]);
  return { cart, update, clearAfterOrder, renewKey, ready: !!key };
}

const EMPTY: StoreCart = { v: 1, items: [], shipTo: null, paymentMethod: null, idempotencyKey: "pending" };

/** Put (or replace) one product's colours and sizes; nothing left = removed. */
export function setCartItem(c: StoreCart, item: CartItem): StoreCart {
  const keep = item.colorQuantities
    .map((r) => ({ color: r.color, quantities: Object.fromEntries(Object.entries(r.quantities).filter(([, n]) => n > 0)) }))
    .filter((r) => Object.keys(r.quantities).length > 0);
  const rest = c.items.filter((i) => i.productId !== item.productId);
  if (keep.length === 0) return { ...c, items: rest };
  const at = c.items.findIndex((i) => i.productId === item.productId);
  const next = { productId: item.productId, colorQuantities: keep };
  if (at < 0) return { ...c, items: [...rest, next] };
  const items = [...c.items];
  items[at] = next;
  return { ...c, items };
}
